import { useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Link } from 'react-router-dom';
import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { MapPin } from 'lucide-react';
import { useT, type I18nKey } from '../../i18n/index.ts';
import { Chip, IconButton } from '../../ui/index.ts';
import { legLocations, type LegLocation } from './legLocations.ts';

const ICON_LINK_CLASS =
  'inline-flex h-7 w-7 items-center justify-center rounded text-paper-mid transition-colors duration-120 hover:bg-raised hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40';

function reasonKey(location: LegLocation): I18nKey {
  switch (location.reason) {
    case 'global':
      return 'guaranteed.leg.reach.global';
    case 'override':
      return 'guaranteed.leg.reach.override';
    case 'org_assigned':
      return 'guaranteed.leg.reach.org_assigned';
    case 'default_fallthrough':
      return 'guaranteed.leg.reach.default_fallthrough';
    case 'unassigned_config':
      return 'guaranteed.leg.reach.unassigned_config';
    case 'shadowed_by_global_exclusive':
      return 'guaranteed.leg.reach.shadowed_by_global_exclusive';
    case null:
      return 'guaranteed.leg.reach.unknown';
  }
}

/**
 * 「在租户路由中查看」: one place jumps straight there; several open a menu
 * grouped 接流量 first, then 不接流量 folded (the usual home of leftover
 * e2e configs, which is exactly what an operator could not find).
 */
export function LegLocationsLink({ leg, testKey }: {
  readonly leg: TargetProbeLegRecord;
  readonly testKey: string;
}): JSX.Element | null {
  const t = useT();
  const [deadOpen, setDeadOpen] = useState(false);
  const locations = legLocations(leg);
  const label = t('guaranteed.leg.locate');
  const only = locations.length === 1 ? locations[0] : undefined;
  if (locations.length === 0) return null;
  if (only !== undefined) {
    return (
      <Link
        to={only.href}
        aria-label={label}
        title={label}
        className={ICON_LINK_CLASS}
        data-testid={`guaranteed-leg-locate-${testKey}`}
      >
        <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
      </Link>
    );
  }
  const live = locations.filter((location) => location.reachable);
  const dead = locations.filter((location) => !location.reachable);
  return (
    <DropdownMenuPrimitive.Root onOpenChange={(open) => { if (!open) setDeadOpen(false); }}>
      <DropdownMenuPrimitive.Trigger asChild>
        <IconButton label={label} size="sm" data-testid={`guaranteed-leg-locate-${testKey}`}>
          <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
        </IconButton>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          data-testid={`guaranteed-leg-locate-menu-${testKey}`}
          className="z-50 max-h-[420px] w-[380px] overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-overlay animate-panel-in"
        >
          <DropdownMenuPrimitive.Label className="px-2.5 pb-1 pt-1.5 text-label font-medium text-paper">
            {t('guaranteed.leg.locate_title')}
          </DropdownMenuPrimitive.Label>
          {live.length === 0 ? null : (
            <DropdownMenuPrimitive.Group data-testid={`guaranteed-leg-locate-live-${testKey}`}>
              <DropdownMenuPrimitive.Label className="px-2.5 pt-1 text-micro text-paper-low">
                {t('guaranteed.leg.locate.group_live')}
              </DropdownMenuPrimitive.Label>
              {live.map((location) => <LocationItem key={location.key} location={location} />)}
            </DropdownMenuPrimitive.Group>
          )}
          {dead.length === 0 ? null : (
            <DropdownMenuPrimitive.Group data-testid={`guaranteed-leg-locate-dead-${testKey}`}>
              <DropdownMenuPrimitive.Item
                onSelect={(event) => {
                  event.preventDefault();
                  setDeadOpen((open) => !open);
                }}
                aria-expanded={deadOpen}
                data-testid={`guaranteed-leg-locate-dead-toggle-${testKey}`}
                className="flex cursor-default select-none items-center gap-1.5 rounded px-2.5 py-1.5 text-micro text-paper-low outline-none data-[highlighted]:bg-active data-[highlighted]:text-paper"
              >
                {deadOpen ? '▾' : '▸'} {t('guaranteed.leg.locate.group_dead', { count: String(dead.length) })}
              </DropdownMenuPrimitive.Item>
              {deadOpen ? dead.map((location) => <LocationItem key={location.key} location={location} />) : null}
            </DropdownMenuPrimitive.Group>
          )}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function LocationItem({ location }: { readonly location: LegLocation }): JSX.Element {
  const t = useT();
  const name = location.layer === 'default'
    ? t('guaranteed.leg.locate.default_name')
    : location.configName ?? location.configId ?? '—';
  const details = [
    t(reasonKey(location)),
    location.assignedOrgs === null ? null : t('guaranteed.leg.locate.orgs', { count: String(location.assignedOrgs) }),
    t('guaranteed.leg.locate.targets', {
      enabled: String(location.enabledTargets),
      disabled: String(location.disabledTargets),
    }),
  ].filter((part): part is string => part !== null);
  return (
    <DropdownMenuPrimitive.Item asChild>
      <Link
        to={location.href}
        data-testid={`guaranteed-leg-location-${location.key}`}
        className="flex cursor-pointer select-none flex-col gap-0.5 rounded px-2.5 py-1.5 text-dense text-paper-mid no-underline outline-none data-[highlighted]:bg-active data-[highlighted]:text-paper"
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium text-paper">{name}</span>
          {location.deprecated ? (
            <Chip tone="neutral" lamp={false}>{t('guaranteed.leg.locate.deprecated')}</Chip>
          ) : null}
        </span>
        <span className="truncate text-micro text-paper-low">{details.join(' · ')}</span>
      </Link>
    </DropdownMenuPrimitive.Item>
  );
}
